import { getCustomerDetails, getCustomerCreditDetails } from '../APICalling/APICalling';
import logger from './logger';

/** Normalizes customer credit API response across flat/nested success shapes. */
const parseCustomerCreditResponse = (response) => {
	if (!response || typeof response !== 'object') {
		return [];
	}
	const payload = response.success !== undefined ? response : response.data;
	if (!payload || payload.success !== true) {
		return [];
	}
	const details = payload.data;
	if (Array.isArray(details)) {
		return details;
	}
	if (details && typeof details === 'object') {
		return [details];
	}
	return [];
};

const parseCreditBalanceFromUserDetails = (user) => {
	const detail = user?.membership_detail;
	if (!detail || typeof detail !== 'object') return null;
	const balance =
		detail.credit_balance ??
		detail.current_credit_balance ??
		user?.credit_balance ??
		user?.current_credit_balance;
	if (balance == null || balance === '') return null;
	const numeric = Number(balance);
	return Number.isFinite(numeric) ? numeric : null;
};

/** Refreshes global customerCreditDetails after top-up (e.g. when ConfirmationPage is shown). */
export const refreshCustomerCreditDetailsFromUser = async (userId, setCustomerCreditDetails) => {
	if (userId == null || userId === '' || typeof setCustomerCreditDetails !== 'function') {
		return;
	}

	try {
		const creditResponse = await getCustomerCreditDetails(userId);
		const creditData = parseCustomerCreditResponse(creditResponse);
		if (creditData.length > 0) {
			setCustomerCreditDetails(creditData);
			return;
		}
	} catch (error) {
		logger.error('Error refreshing customer credit details:', error);
	}

	try {
		const response = await getCustomerDetails(userId);
		if (response?.data?.success && response?.data?.data) {
			const user = response.data.data;
			const balance = parseCreditBalanceFromUserDetails(user);
			const membershipDetail = user.membership_detail || {};
			const fullName = `${user.name || ''} ${user.lastname || ''}`.trim();
			if (balance != null) {
				setCustomerCreditDetails([
					{
						user_name: fullName || user.name || '',
						membership_number:
							membershipDetail.membership_number ?? user.membership_number ?? null,
						current_credit_balance: balance,
						credit_balance: balance,
					},
				]);
			}
		}
	} catch (error) {
		logger.error('Error refreshing customer credit from user details:', error);
	}
};

/** Remaining checkout due after partial payments; falls back to saved amountDue when history is empty. */
export const computeRemainingCheckoutAmount = (
	grandTotal,
	paymentHistory,
	fallbackAmountDue
) => {
	const total = Number(grandTotal) || 0;
	const history = Array.isArray(paymentHistory) ? paymentHistory : [];
	const paid = history.reduce((sum, entry) => sum + (Number(entry?.amount) || 0), 0);

	if (paid > 0) {
		return Math.max(0, total - paid);
	}

	const fallback = parseFloat(fallbackAmountDue);
	if (Number.isFinite(fallback) && fallback >= 0) {
		return fallback;
	}

	return Math.max(0, total);
};

/** Map Others Membership dropdown row to CustomerDetailsModal customer (user id only, not membership id). */
export const membershipClientToTopupCustomer = (client) => {
	if (!client || typeof client !== 'object') return null;
	const userId = client.user_id;
	if (userId == null || userId === '') return null;

	const displayName = String(client.user_name || client.name || '').trim();
	const nameParts = displayName.split(/\s+/).filter(Boolean);
	const firstName = nameParts[0] || displayName;
	const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

	return {
		id: userId,
		name: firstName,
		lastname: lastName,
		contact_number: client.user_phone ?? client.contact_number ?? '',
		email: client.email ?? '',
		membership_number: client.membership_number ?? null,
		membership_detail: client.membership_detail ?? null,
	};
};

/** Partial update for Others Membership client balance after top-up. */
export const fetchMembershipClientCreditPatch = async (userId) => {
	if (userId == null || userId === '') return null;

	try {
		const creditResponse = await getCustomerCreditDetails(userId);
		const creditData = parseCustomerCreditResponse(creditResponse);
		const row = creditData[0];
		if (row) {
			const balance = row.current_credit_balance ?? row.credit_balance ?? null;
			if (balance != null) {
				const patch = {
					credit_balance: balance,
					current_credit_balance: balance,
				};
				if (row.is_password_set !== undefined && row.is_password_set !== null) {
					patch.is_password_set = row.is_password_set;
				}
				return patch;
			}
		}
	} catch (error) {
		logger.error('Error fetching membership client credit patch:', error);
	}

	try {
		const response = await getCustomerDetails(userId);
		if (!response?.data?.success || !response?.data?.data) return null;
		const user = response.data.data;
		const balance = parseCreditBalanceFromUserDetails(user);
		if (balance == null) return null;
		return {
			credit_balance: balance,
			current_credit_balance: balance,
			is_password_set: user.is_password_set,
		};
	} catch (error) {
		logger.error('Error fetching membership client credit patch from user:', error);
		return null;
	}
};
